import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { SelectComponent } from "@/components/select/Select";
import { TextArea } from "@/components/text-area/TextArea";
import { AppColors } from "@/styles/colors";
import { Card, Field, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppRoutes } from "@/constants/routes";
import { useNavigate } from "react-router-dom";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { createRequestHandler } from "./handler-functions";
import { getSucursales, Sucursal } from "@/services/accounts.service";
import {
  getOptionsHandler,
  loadOptionsHandler,
} from "../schema/handler-functions";
import { AutocompleteSelect } from "@/components/select/AutocompleteSelect";
import { getUsuariosMobileByCuenta } from "@/services/users.service";
import { formatCurrencyInputToNumber, isEmpty } from "@/utils/query-string";
import { checkRequestCreationSchema } from "../schema/check-requests.schema";
import {
  RequestTypeDescription,
  RequestTypes,
} from "@/constants/entity-status";
import DatePicker from "@/components/date-picker";
import dayjs from "dayjs";
import { useRoles } from "@/hooks/useRoles";
import { validateMinCurrencyAmount } from "@/utils/request-validations";

export const CheckRequestCreationPage = () => {

  const [isActive, setIsActive] = useState<boolean>(false);

  const [loadingSucursales, setLoadingSucursales] = useState<boolean>(false);

  const [sucursales, setSucursales] = useState<Sucursal[] | null>([]);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchSucursales = async () => {
    setLoadingSucursales(true);

    const response = await getSucursales();
    setSucursales(response);

    setLoadingSucursales(false);
  };

  const { fetchPermissions } = useRoles();

  const navigate = useNavigate();
  const {
    register,
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(checkRequestCreationSchema),
  });

  const [loadingCreateCheckRequest, setLoadingCreateCheckRequest] =
    useState<boolean>(false);

  const handleCreateCheckRequest = async () => {
    await createRequestHandler({
      navigate,
      setLoadingCreateCheckRequest,
      watchedValues,
      fetchPermissions,
    });
  };

  const watchedValues = useWatch({ control });

  const hasNoFilledValues = useMemo(() => {
    const { sucursal, account, request_amount, fechaVto, tipoSolicitud } =
      watchedValues || {};

    return (
      Boolean(
        isEmpty(sucursal) && tipoSolicitud?.[0] !== RequestTypes.E_CHEQ,
      ) ||
      isEmpty(account) ||
      isEmpty(request_amount) ||
      isEmpty(fechaVto) ||
      isEmpty(tipoSolicitud) ||
      formatCurrencyInputToNumber(watchedValues?.request_amount) < 1
    );
  }, [watchedValues]);

  useEffect(() => {
    validateMinCurrencyAmount({
      clearErrors,
      setError,
      watchedAmount: watchedValues?.request_amount,
      fieldName: "request_amount",
      message: "El valor debe ser mayor o igual a $1.",
      min: 1,
    });
  }, [watchedValues?.request_amount, errors]);

  const handleCancel = () => {
    navigate(AppRoutes.CHECK_REQUESTS);
  };

  const [loadingPersons, setLoadingPersons] = useState<boolean>(false);

  const [cuentaUsuarios, setCuentaUsuarios] = useState<any>([]);

  const [loadingCuentaUsuarios, setLoadingCuentaUsuarios] =
    useState<boolean>(false);

  const getUsuariosByCuenta = async () => {
    setLoadingCuentaUsuarios(true);
    const response = await getUsuariosMobileByCuenta({
      cuenta: watchedValues?.account?.value,
    });

    setCuentaUsuarios(response);
    setLoadingCuentaUsuarios(false);
  };

  const getOptions = async (
    inputValue: string,
  ): Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  > => {
    return getOptionsHandler({
      inputValue,
    });
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions,
      inputValue,
      setLoadingPersons,
      timeoutRef,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchSucursales();
    }
  }, [isActive]);

  const watchedAccount = useWatch({ control, name: "account" });

  const watchedCheckType = useWatch({ control, name: "tipoSolicitud" });

  useEffect(() => {
    if (watchedCheckType?.[0] === RequestTypes.E_CHEQ) {
      setValue("sucursal", []);
    }
  }, [watchedCheckType]);

  useEffect(() => {
    if (watchedAccount) {
      setValue("usuarioMobile", []);
      getUsuariosByCuenta();
    }
  }, [watchedAccount]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar solicitud de cheque"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateCheckRequest}
        disableAdd={hasNoFilledValues}
        loading={loadingCreateCheckRequest}
      />
      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 1fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <Field.Root invalid={!!errors?.account?.message} gap="12px">
                <Field.Label>
                  {" "}
                  <Text fontSize="16px">Cuenta</Text>
                </Field.Label>

                <AsyncAutocompleteSelect
                  control={control}
                  loadOptions={loadOptions}
                  name="account"
                  placeholder="Buscar..."
                  isLoading={loadingPersons}
                />

                <Field.ErrorText>
                  {typeof errors?.account?.message === "string"
                    ? errors.account.message
                    : ""}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>
            <GridItem paddingX="16px" paddingY="8px">
              <Field.Root invalid={!!errors?.tipoSolicitud?.message}>
                <Field.Label>
                  <Text fontSize="16px">Tipo de cheque</Text>
                </Field.Label>
                <SelectComponent
                  fullWidth
                  items={[
                    {
                      label: RequestTypeDescription.PHYSICAL_CHECK,
                      value: RequestTypes.PHYSICAL_CHECK,
                    },
                    {
                      label: RequestTypeDescription.E_CHEQ,
                      value: RequestTypes.E_CHEQ,
                    },
                  ]}
                  menuMaxHeight="250px"
                  name="tipoSolicitud"
                  control={control}
                  closeOnSelect
                  clearable
                  size="xs"
                  minHeight="40px"
                  placeholder="Seleccionar tipo de solicitud"
                  loading={loadingCreateCheckRequest}
                  disabled={loadingCreateCheckRequest}
                />
                <Field.ErrorText>
                  {typeof errors?.tipoSolicitud?.message === "string"
                    ? errors.tipoSolicitud.message
                    : ""}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>
            {watchedCheckType?.[0] !== RequestTypes.E_CHEQ && (
              <GridItem paddingX="16px" paddingY="8px">
                <Field.Root invalid={!!errors?.sucursal?.message}>
                  <Field.Label>
                    <Text fontSize="16px">Sucursal</Text>
                  </Field.Label>
                  <SelectComponent
                    fullWidth
                    items={
                      sucursales?.map((sucursal) => ({
                        label: sucursal?.nombre,
                        value: sucursal?.clave,
                      })) ?? []
                    }
                    menuMaxHeight={{
                      xs: "350px",
                      sm: "420px",
                      md: "500px",
                      lg: "600px",
                      xl: "1000px",
                    }}
                    name="sucursal"
                    control={control}
                    closeOnSelect
                    clearable
                    size="xs"
                    minHeight="40px"
                    placeholder="Seleccionar sucursal"
                    loading={loadingSucursales}
                    disabled={loadingSucursales}
                  />
                  <Field.ErrorText>
                    {typeof errors?.sucursal?.message === "string"
                      ? errors.sucursal.message
                      : ""}
                  </Field.ErrorText>
                </Field.Root>
              </GridItem>
            )}

            <GridItem paddingX="16px" paddingY="8px">
              <Field.Root
                invalid={!!errors?.request_amount?.message}
                gap="12px"
              >
                <Field.Label>
                  <Text fontSize="16px">Monto</Text>
                </Field.Label>
                <InputCurrency
                  control={control}
                  name="request_amount"
                  height="40px"
                  maxLength={20}
                />

                <Field.ErrorText>
                  {typeof errors?.request_amount?.message === "string"
                    ? errors.request_amount.message
                    : ""}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>
            <GridItem paddingX="16px" paddingY="8px">
              <DatePicker
                control={control}
                name="fechaVto"
                height="40px"
                labelText="Fecha de vencimiento"
                maxDate={dayjs().add(1, "year")}
                disablePortal={false}
                maxYear={dayjs().year() + 1}
                minDate={dayjs()}
              />
            </GridItem>
            <GridItem paddingX="16px" paddingY="8px">
              <Field.Root invalid={!!errors?.usuarioMobile?.message}>
                <Field.Label>
                  <Text>Usuario</Text>
                </Field.Label>
                <AutocompleteSelect
                  control={control}
                  inputName="usuarioMobile"
                  loading={loadingCuentaUsuarios}
                  disabled={!watchedValues?.account || loadingCuentaUsuarios}
                  multiple={false}
                  items={
                    cuentaUsuarios?.map((cu: any) => ({
                      label: `${cu?.nombre} ${cu?.apellido}`,
                      value: cu?.id,
                    })) ?? []
                  }
                />
              </Field.Root>
            </GridItem>
          </Grid>
          <Flex gap={"50px"}></Flex>
        </Card.Body>
      </Card.Root>
      <Flex flexDir="column" gap="12px">
        <Text fontWeight={700} fontSize="20px">
          Detalle
        </Text>
        <TextArea
          errors={errors}
          inputName="request_detail"
          maxLength={50}
          register={register}
          placeholder="Ingresar una descripción de la solicitud"
          helperText="Máximo 50 caracteres"
        />
      </Flex>
    </Flex>
  );
};
